import uuid from 'uuid/v4';
import config from '../utils/config';
import i18n from '../utils/i18n';
import getSafe from '../utils/getSafe';
import sentry from '../utils/sentry';
import { formatHomeError } from '../utils/pages/restaurants';
import {
  buildLocation,
  getFulfillmentMethod,
  getFulfillmentTime,
  getCollectionParams,
  buildSupportedFulfillmentMethods,
  buildSupportedFulfillmentTimestamps,
  buildSelectedFilters,
} from '../selectors/homeSelectors';

import { getHomeFeed, getFulfillmentTimes, getSearchResults } from '../queries/home';

import getPlatform from '../utils/getPlatform';
import getColumnCount from '../utils/home/getColumnCount';
import { parseFulfillmentMethod, getFulfillmentMethodParam } from '../utils/fulfillmentMethod';
import { parseFulfillmentTime, getFulfillmentTimeParam } from '../utils/fulfillmentTime';

import { isFeatureActive } from '../selectors/configSelectors';
import { CO_HOME_MOCK_ENDPOINT } from '../flags/features';
import { FULFILLMENT_METHOD_COLLECTION } from '../utils/constants';

// eslint-disable-next-line import/no-cycle
import { requestHomeMapView, toggleMapView } from './homeMapActions';
import { getMealCardIssuers } from './mealCardActions';
import { trackTappedPickupBanner } from './tracking/home';
// eslint-disable-next-line import/no-cycle
import { trackToggledShowMapView } from './tracking/homeMap';
import { trackTappedTimePicker } from './tracking/dateTimePicker';

import { displayNotification } from './notificationActions';

import {
  FETCH_HOME_FEED,
  FETCH_HOME_FEED_FAILURE,
  FETCH_HOME_SEARCH_RESULTS,
  FETCH_HOME_FULFILLMENT_TIMES,
  SET_HOME_FILTERS,
  SET_HOME_QUERY,
  SHOW_DATE_TIME_MODAL,
  HIDE_DATE_TIME_MODAL,
  SHOW_LOGISTICS_MODAL,
  HIDE_LOGISTICS_MODAL,
  HIDE_LOGISTICS_MODALS,
  UPDATE_LOCATION_DETAILS,
  SHOW_VOTE_AREA_MODAL,
  CLEAR_HOME_SEARCH_RESULTS,
} from './types';

const { Sentry, captureException } = sentry();

const API_TIMEOUT = 10000;

export function setInitialHomeFilters(q) {
  return {
    type: SET_HOME_FILTERS,
    payload: {
      fulfillmentMethod: q.fulfillment_method,
      geohash: q.geohash,
      postcode: q.postcode,
      city: q.city,
      neighborhood: q.neighborhood,
      deliveryTime: q.delivery_time,
      // TODO: delivery_time => time or fulfillment_time when BE supports it
      fulfillmentTime: q.delivery_time,
      query: q.query,
    },
  };
}

export function setHomeQuery({ query }) {
  return {
    type: SET_HOME_QUERY,
    payload: {
      query,
    },
  };
}

export const buildQueryOptions = ({ state, query, url, columnCount }) => {
  const options = {
    query,
    web_column_count: getColumnCount(columnCount),
  };

  return {
    fulfillment_methods: buildSupportedFulfillmentMethods(state),
    location: buildLocation(state),
    options,
    url,
    uuid: uuid(),
  };
};

// Build the query parameters to add to the url.
export const buildQueryParams = ({
  state,
  selectedFilters,
  clearCollection,
  fulfillmentMethod,
  fulfillmentTime,
}) => {
  const queryParams = [...selectedFilters];

  let fulfillmentMethodParam;
  if (fulfillmentMethod) {
    const supportedFulfillmentMethods = buildSupportedFulfillmentMethods(state);
    const parsedFulfillmentMethod = parseFulfillmentMethod(
      supportedFulfillmentMethods,
      fulfillmentMethod,
    );
    fulfillmentMethodParam = getFulfillmentMethodParam(parsedFulfillmentMethod);
  } else {
    fulfillmentMethodParam = getFulfillmentMethodParam(getFulfillmentMethod(state));
  }

  if (fulfillmentMethodParam) {
    queryParams.push(fulfillmentMethodParam);
  }

  let fulfillmentTimeParam;
  if (fulfillmentTime) {
    const supportedFulfillmentTimestamps = buildSupportedFulfillmentTimestamps(state);
    const parsedFulfillmentTime = parseFulfillmentTime(
      supportedFulfillmentTimestamps,
      fulfillmentTime,
    );
    fulfillmentTimeParam = getFulfillmentTimeParam(parsedFulfillmentTime);
  } else {
    fulfillmentTimeParam = getFulfillmentTimeParam(getFulfillmentTime(state));
  }

  if (fulfillmentTimeParam) {
    queryParams.push(fulfillmentTimeParam);
  }

  const { geohash, postcode } = buildLocation(state);

  if (geohash) {
    queryParams.push(`geohash=${geohash}`);
  } else if (postcode) {
    queryParams.push(`postcode=${postcode}`);
  }

  // collectionParams will contain the collection parameter from the previous query which we can apply to subsequent queries.
  const collectionParams = getCollectionParams(state);

  // clearCollection will be set to true when the user exits from a collection view or the user clicks into a new collection. Only one collection parameter can be applied at any time.
  if (collectionParams && !clearCollection) {
    queryParams.push(collectionParams);
  }

  return queryParams;
};

export const buildURL = ({
  state,
  selectedFilters,
  clearCollection,
  fulfillmentMethod,
  fulfillmentTime,
}) => {
  if (typeof window !== 'undefined') {
    const queryParams = buildQueryParams({
      state,
      selectedFilters,
      clearCollection,
      fulfillmentMethod,
      fulfillmentTime,
    });
    return `${window.location.origin}${window.location.pathname}?${queryParams.join('&')}`.replace(
      '.dev',
      '',
    );
  }
  return '';
};

export function requestSearchResults({ query = '' } = {}) {
  return (dispatch, getState) => {
    const state = getState();

    const isMock = isFeatureActive(state, CO_HOME_MOCK_ENDPOINT);
    const endpoint = `/consumer/graphql/${isMock ? 'mock' : ''}`;
    const url = buildURL({ state, selectedFilters: [`query=${query}`], clearCollection: true });
    const queryOptions = buildQueryOptions({ state, query, url });
    const options = {
      method: 'POST',
      timeout: API_TIMEOUT,
      body: getSearchResults(queryOptions),
    };

    return dispatch({
      type: FETCH_HOME_SEARCH_RESULTS,
      host: config.homeApiHost,
      endpoint,
      options,
    });
  };
}

export function requestHomeFeed({
  query = '',
  url = '',
  columnCount = 1,
  clearCollection = false,
  selectedFilters = [],
  fulfillmentMethod,
  fulfillmentTime,
} = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const isMock = isFeatureActive(state, CO_HOME_MOCK_ENDPOINT);
    const endpoint = `/consumer/graphql/${isMock ? 'mock' : ''}`;

    const queryUrl =
      url ||
      buildURL({
        state,
        selectedFilters,
        clearCollection,
        fulfillmentMethod,
        fulfillmentTime,
      });

    const queryOptions = buildQueryOptions({
      state,
      query,
      url: queryUrl,
      columnCount,
    });

    const options = {
      method: 'POST',
      timeout: API_TIMEOUT,
      body: getHomeFeed(queryOptions),
    };

    return dispatch({
      type: FETCH_HOME_FEED,
      host: config.homeApiHost,
      endpoint,
      options,
    }).catch((error) => {
      /*
        Any client side API requests that error will be caught in this block.
        The error caught will be thrown in the `createExceptionHandler` function - app/middleware/api.js.
        On Home we always want to respond to a failed request by dispatching the FAILED action types.
      */

      const status = error.status || 500;
      const data = error.data || {};
      const message = data.message || error.message;

      dispatch({
        type: FETCH_HOME_FEED_FAILURE,
        status,
        message,
        payload: {
          ...data,
        },
      });

      /*
        Format the error using the same error classes that we use for API failures on the Server Side Render.
        This will give us consistency in errors logged on the Server and Client from the Home API.
      */
      const homeError = formatHomeError({
        status,
        message: message || getSafe(() => data.errors[0].message),
        data: data.errors,
      });

      Sentry.withScope((scope) => {
        scope.setExtras({
          'home.queryUrl': queryUrl,
          'home.neighborhood': queryOptions.location.neighborhood_uname,
          'home.trackingId': queryOptions.uuid,
          statusCode: homeError.status,
        });

        captureException(homeError.error);
      });

      return displayNotification({
        message: i18n.gettext('global.notification.generic_error'),
        type: 'error',
      });
    });
  };
}

export function requestFulfillmentTimes() {
  return (dispatch, getState) => {
    const state = getState();

    const isMock = isFeatureActive(state, CO_HOME_MOCK_ENDPOINT);
    const endpoint = `/consumer/graphql/${isMock ? 'mock' : ''}`;

    const options = {
      method: 'POST',
      timeout: API_TIMEOUT,
      body: getFulfillmentTimes({
        fulfillmentMethods: buildSupportedFulfillmentMethods(state),
        location: buildLocation(state),
        uuid: uuid(),
      }),
    };

    return dispatch({
      type: FETCH_HOME_FULFILLMENT_TIMES,
      host: config.homeApiHost,
      endpoint,
      options,
    });
  };
}

export function clearSearchResults() {
  return (dispatch) => {
    dispatch({
      type: CLEAR_HOME_SEARCH_RESULTS,
    });
  };
}

export function requestInitialHomeFeed() {
  return (dispatch, getState) => {
    const { request } = getState();
    const url = `${request.host}/${request.currentUrl}`.replace('.dev', '').replace(/\/$/, '');
    /*
      Check if the device is a mobile, set the column count to 1.
      The column count property will hydrate the list layouts in the Home API response with a more appropriate number of blocks.
      On Desktop we'd like more blocks per list to reduce whitespace.
    */
    const isMobilePlatform = (userAgent) => ['ios', 'android'].includes(getPlatform(userAgent));
    const columnCount = isMobilePlatform(request.userAgent) ? 1 : 4;
    return dispatch(requestHomeFeed({ url, columnCount }));
  };
}

export function applyFilters(selectedFilters) {
  return (dispatch) => dispatch(requestHomeFeed({ selectedFilters }));
}

// The second argument to this action is there for easy testability.
export const setHomeFulfillmentTime = ({ fulfillmentTime }, requestFn = requestHomeFeed) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const selectedFilters = buildSelectedFilters(state);
  return dispatch(requestFn({ selectedFilters, fulfillmentTime }));
};

// The second argument to this action is there for easy testability.
export const setHomeFulfillmentMethod = ({ fulfillmentMethod }, requestFn = requestHomeFeed) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const selectedFilters = buildSelectedFilters(state);
  return dispatch(requestFn({ selectedFilters, fulfillmentMethod }));
};

export function toggleDateTimeModal(isOpen) {
  return (dispatch) => {
    dispatch({
      type: isOpen ? SHOW_DATE_TIME_MODAL : HIDE_DATE_TIME_MODAL,
      payload: {},
    });
    if (isOpen) {
      dispatch(trackTappedTimePicker('restaurant list'));
    }
  };
}

export function toggleLogisticsModal(isOpen) {
  return {
    type: isOpen ? SHOW_LOGISTICS_MODAL : HIDE_LOGISTICS_MODAL,
    payload: {},
  };
}

export function hideLogisticsModals() {
  return {
    type: HIDE_LOGISTICS_MODALS,
    payload: {},
  };
}

export function showVoteAreaWithData() {
  return (dispatch, getState) => {
    const state = getState();
    const { postcode, lat, lon } = state.home.feed.meta.location;

    dispatch({
      type: UPDATE_LOCATION_DETAILS,
      payload: {
        post_code: postcode || '',
        formatted_address: '',
        lng: lon,
        lat,
      },
    });

    return dispatch({
      type: SHOW_VOTE_AREA_MODAL,
    });
  };
}

// To keep in sync with the SUPPORTED_ACTIONS list in
// the `app/queries/home/constants.js` file.
export const actionCreators = {
  CHANGE_DELIVERY_TIME: () => toggleDateTimeModal(true),
  SHOW_MEAL_CARD_ISSUERS: () => getMealCardIssuers(),
  CLEAR_FILTERS: () => requestHomeFeed({ clearCollection: true }),
  NO_DELIVERY_YET: () => showVoteAreaWithData(),
  SHOWCASE_PICKUP: () => (dispatch) => {
    dispatch(trackTappedPickupBanner());
    dispatch(setHomeFulfillmentMethod({ fulfillmentMethod: FULFILLMENT_METHOD_COLLECTION }));
  },
  SHOW_HOME_MAP_VIEW: () => (dispatch) => {
    dispatch(trackToggledShowMapView());
    dispatch(toggleMapView());
    dispatch(requestHomeMapView());
  },
};

export function applyActionTarget(actionType) {
  return (dispatch, getState) => {
    if (actionType === 'RETRY_GET_HOME_FEED' && typeof window !== 'undefined') {
      return dispatch(requestHomeFeed({ url: window.location.href }));
    }
    const actionCreator = actionCreators[actionType];
    const state = getState();

    if (!actionCreator) {
      return undefined;
    }

    return dispatch(actionCreator(state));
  };
}
